import {Component, EventEmitter, Injector, Output, ViewChild, OnInit, Input, OnDestroy} from '@angular/core';
import { AppConsts } from '@shared/AppConsts';
import { AppComponentBase } from '@shared/common/app-component-base';
import {
    OrganizationUnitDto,
    PasswordComplexitySetting,
    ProfileServiceProxy,
    UserRoleDto,
    UserServiceProxy,
    TenantSettingsEditDto,
    TenantSettingsServiceProxy,
    VirtualBankAccountDto,
    ICreateOrUpdateUserInputOfUserEditDto,
    CreateOrUpdateUserInputOfUserEditDto,
    UserEditDto,
    VirtualBankAccountEditDto
} from '@shared/service-proxies/service-proxies';
import { ModalDirective } from 'ngx-bootstrap/modal';
import _ from 'lodash';
import {distinctUntilChanged, finalize, pairwise, startWith} from 'rxjs/operators';
import { TreeNode } from 'primeng/api';
import {FormBuilder, FormGroup, NgForm} from '@angular/forms';
import {BasePermissionDefinition} from '@shared/permission/base-permission-definition';
import {BillingInfoValidator} from '@app/shared/common/billing-information/billing-information.component';
import {Subscription} from 'rxjs';
import {
    IOrganizationUnitsTreeComponentData,
    OrganizationUnitsTreeComponent
} from '@app/admin/shared/organization-unit-tree.component';

@Component({
    selector: 'createOrEditUserModal',
    templateUrl: './create-or-edit-user-modal.component.html',
    styles: [`.user-edit-dialog-profile-image {
             margin-bottom: 20px;
        }`
    ]
})
export class CreateOrEditUserModalComponent extends AppComponentBase implements OnInit, OnDestroy {
    protected readonly BasePermissionDefinition = BasePermissionDefinition;

    @ViewChild('createOrEditModal', {static: true}) modal: ModalDirective;
    @ViewChild('organizationUnitTree', {static: false}) organizationUnitTree: OrganizationUnitsTreeComponent;

    @Output() modalSave: EventEmitter<any> = new EventEmitter<any>();
    @Output() openPaymentTerms: EventEmitter<any> = new EventEmitter<any>();
    @Output() openSelectionOU: EventEmitter<any> = new EventEmitter<any>();

    @ViewChild('userForm') userForm: NgForm;

    @Input() billingInfoValidator?: BillingInfoValidator;

    @Input() isCustomerMode?: boolean = false;

    active = false;
    saving = false;
    isSettingInvoiceEnabled = true;
    temporarySavedData = false;

    canChangeUserName = true;
    isTwoFactorEnabled: boolean = this.setting.getBoolean('Abp.Zero.UserManagement.TwoFactorLogin.IsEnabled');
    isLockoutEnabled: boolean = this.setting.getBoolean('Abp.Zero.UserManagement.UserLockOut.IsEnabled');
    passwordComplexitySetting: PasswordComplexitySetting = new PasswordComplexitySetting();

    usingDefaultTimeZone = false;
    initialTimeZone: string = null;

    user: UserEditDto = new UserEditDto();
    roles: UserRoleDto[];
    sendActivationEmail = true;
    setRandomPassword = true;
    passwordComplexityInfo = '';
    profilePicture: string;
    activeTabIndex: number = (abp.clock.provider.supportsMultipleTimezone) ? 0 : 1;
    settings: TenantSettingsEditDto = undefined;
    loading: Boolean = false;
    billInfo: VirtualBankAccountDto = new VirtualBankAccountDto();


    allOrganizationUnits: OrganizationUnitDto[];
    memberedOrganizationUnits: string[];
    userPasswordRepeat = '';
    isValidateMode = false;

    recentTenantsData: any;

    vatRequired: any;
    allowRemovingOrganizationUnits = true;

    @ViewChild('billingInformation') public billingInformationForm: NgForm;

    billingInfoForm!: FormGroup;

    private subscriptions: Subscription[] = [];

    get userInfoTabName(): string {
        if (this.isCustomerMode) {
            return 'ContactInformation';
        }

        return 'UserInformations';
    }
    get invoiceTabName(): string {
        if (this.isCustomerMode) {
            return 'CompanyInformation';
        }

        return 'Invoice';
    }

    constructor(
        injector: Injector,
        private _userService: UserServiceProxy,
        private _profileService: ProfileServiceProxy,
        private _tenantSettingsService: TenantSettingsServiceProxy
        , private fb: FormBuilder
    ) {
        super(injector);
    }

    ngOnInit() {
        this.getSettings();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    onEmailChanged(): void {
        if (this.isCustomerMode) {
            this.billInfo.invoiceEAddress = this.user.emailAddress;
            this.billingInfoForm.patchValue({invoiceEAddress: this.user.emailAddress}, { emitEvent: false });
        }
    }

    private _initBillingInfo() {
        // Ensure billingInfoValidator is an instance of BillingInfoValidator
        if (!(this.billingInfoValidator instanceof BillingInfoValidator)) {
            this.billingInfoValidator = new BillingInfoValidator(this.billingInfoValidator);
        }

        this.billingInfoForm = this.fb.group({
            visitorLegalName: ['', this.billingInfoValidator.getValidator('requiredVisitorLegalName', 'requiredVisitorBlock')],
            visitorContactName: ['', this.billingInfoValidator.getValidator('requiredVisitorContactName', 'requiredVisitorBlock')],
            visitorAddress: ['', this.billingInfoValidator.getValidator('requiredVisitorAddress', 'requiredVisitorBlock')],
            visitorAddressHouseNumber: ['', this.billingInfoValidator.getValidator('requiredVisitorAddressHouseNumber', 'requiredVisitorBlock')],
            visitorAddressPostcode: ['', this.billingInfoValidator.getValidator('requiredVisitorAddressPostcode', 'requiredVisitorBlock')],
            visitorAddressCity: ['', this.billingInfoValidator.getValidator('requiredVisitorAddressCity', 'requiredVisitorBlock')],
            visitorAddressCountry: ['', this.billingInfoValidator.getValidator('requiredVisitorAddressCountry', 'requiredVisitorBlock')],

            invoiceContactName: ['', this.billingInfoValidator.getValidator('requiredInvoiceContactName', 'requiredInvoiceBlock')],
            invoiceAddress: ['', this.billingInfoValidator.getValidator('requiredInvoiceAddress', 'requiredInvoiceBlock')],
            invoiceAddressHouseNumber: ['', this.billingInfoValidator.getValidator('requiredInvoiceAddressHouseNumber', 'requiredInvoiceBlock')],
            invoiceAddressPostcode: ['', this.billingInfoValidator.getValidator('requiredInvoiceAddressPostcode', 'requiredInvoiceBlock')],
            invoiceAddressCity: ['', this.billingInfoValidator.getValidator('requiredInvoiceAddressCity', 'requiredInvoiceBlock')],
            invoiceAddressCountry: ['', this.billingInfoValidator.getValidator('requiredInvoiceAddressCountry', 'requiredInvoiceBlock')],

            deliveryContactName: ['', this.billingInfoValidator.getValidator('requiredDeliveryContactName', 'requiredDeliveryBlock')],
            deliveryAddress: ['', this.billingInfoValidator.getValidator('requiredDeliveryAddress', 'requiredDeliveryBlock')],
            deliveryAddressHouseNumber: ['', this.billingInfoValidator.getValidator('requiredDeliveryAddressHouseNumber', 'requiredDeliveryBlock')],
            deliveryAddressPostcode: ['', this.billingInfoValidator.getValidator('requiredDeliveryAddressPostcode', 'requiredDeliveryBlock')],
            deliveryAddressCity: ['', this.billingInfoValidator.getValidator('requiredDeliveryAddressCity', 'requiredDeliveryBlock')],
            deliveryAddressCountry: ['', this.billingInfoValidator.getValidator('requiredDeliveryAddressCountry', 'requiredDeliveryBlock')],

            contactName: ['', this.billingInfoValidator.getValidator('requiredContactName', 'requiredSearchBlock')],
            phoneNumber: ['', this.billingInfoValidator.getValidator('requiredPhoneNumber', 'requiredSearchBlock')],
            phoneNumberSecondary: ['', this.billingInfoValidator.getValidator('requiredPhoneNumberSecondary', 'requiredSearchBlock')],
            invoiceEAddress: ['', this.billingInfoValidator.getValidator('requiredInvoiceEAddress', 'requiredSearchBlock')],
            invoiceRememberEInvoiceAddress: ['', this.billingInfoValidator.getValidator('requiredInvoiceRememberEInvoiceAddress', 'requiredSearchBlock')],

            accountBankName: ['', this.billingInfoValidator.getValidator('requiredAccountBankName', 'requiredCompanyDataBlock')],
            accountNumber: ['', this.billingInfoValidator.getValidator('requiredAccountNumber', 'requiredCompanyDataBlock')],
            registrationNo: ['', this.billingInfoValidator.getValidator('requiredRegistrationNo', 'requiredCompanyDataBlock')],
            taxVatNo: ['', this.billingInfoValidator.getValidator('requiredTaxVatNo', 'requiredCompanyDataBlock')],
        });

        this.setupVisitorValueChangeSubscriptions();
        //this.setupInvoiceValueChangeSubscriptions();
        //this.setupDeliveryValueChangeSubscriptions();
    }

    private _mapToVirtualBank() {
        const data = this.billingInfoForm.value;

        this.billInfo.contactName = data.contactName;
        this.billInfo.legalName = data.visitorLegalName;
        this.billInfo.address = data.visitorAddress;
        this.billInfo.addressHouseNumber = data.visitorAddressHouseNumber;
        this.billInfo.addressPostcode = data.visitorAddressPostcode;
        this.billInfo.addressCity = data.visitorAddressCity;
        this.billInfo.addressCountry = data.visitorAddressCountry;
        this.billInfo.invoiceContactName = data.invoiceContactName;
        this.billInfo.invoiceAddress = data.invoiceAddress;
        this.billInfo.invoiceAddressHouseNumber = data.invoiceAddressHouseNumber;
        this.billInfo.invoiceAddressPostcode = data.invoiceAddressPostcode;
        this.billInfo.invoiceAddressCity = data.invoiceAddressCity;
        this.billInfo.invoiceAddressCountry = data.invoiceAddressCountry;
        this.billInfo.deliveryContactName = data.deliveryContactName;
        this.billInfo.deliveryAddress = data.deliveryAddress;
        this.billInfo.deliveryAddressHouseNumber = data.deliveryAddressHouseNumber;
        this.billInfo.deliveryAddressPostcode = data.deliveryAddressPostcode;
        this.billInfo.deliveryAddressCity = data.deliveryAddressCity;
        this.billInfo.deliveryAddressCountry = data.deliveryAddressCountry;
        this.billInfo.phoneNumber = data.phoneNumber;
        this.billInfo.phoneNumberSecondary = data.phoneNumberSecondary;
        this.billInfo.invoiceEAddress = data.invoiceEAddress;
        this.billInfo.invoiceRememberEInvoiceAddress = data.invoiceRememberEInvoiceAddress;
        this.billInfo.accountBankName = data.accountBankName;
        this.billInfo.accountNumber = data.accountNumber;
        this.billInfo.registrationNo = data.registrationNo;
        this.billInfo.taxVatNo = data.taxVatNo;

        return this.billInfo;
    }

    private _setupBillingForm() {
        console.log(this.billInfo);
        const updates: { [key: string]: any } = {};

        // Contact Information
        if (this.billInfo.contactName) {
            updates['contactName'] = this.billInfo.contactName;
            updates['visitorContactName'] = this.billInfo.contactName;
        }
        if (this.billInfo.invoiceEAddress) {
            updates['invoiceEAddress'] = this.billInfo.invoiceEAddress;
        }
        if (this.billInfo.invoiceRememberEInvoiceAddress) {
            updates['invoiceRememberEInvoiceAddress'] = this.billInfo.invoiceRememberEInvoiceAddress;
        }
        if (this.billInfo.phoneNumber) {
            updates['phoneNumber'] = this.billInfo.phoneNumber;
        }
        if (this.billInfo.phoneNumberSecondary) {
            updates['phoneNumberSecondary'] = this.billInfo.phoneNumberSecondary;
        }
        if (this.billInfo.accountBankName) {
            updates['accountBankName'] = this.billInfo.accountBankName;
        }
        if (this.billInfo.accountNumber) {
            updates['accountNumber'] = this.billInfo.accountNumber;
        }
        if (this.billInfo.registrationNo) {
            updates['registrationNo'] = this.billInfo.registrationNo;
        }
        if (this.billInfo.taxVatNo) {
            updates['taxVatNo'] = this.billInfo.taxVatNo;
        }
        if (this.billInfo.legalName) {
            updates['visitorLegalName'] = this.billInfo.legalName;
        }
        if (this.billInfo.address) {
            updates['visitorAddress'] = this.billInfo.address;
        }
        if (this.billInfo.addressHouseNumber) {
            updates['visitorAddressHouseNumber'] = this.billInfo.addressHouseNumber;
        }
        if (this.billInfo.addressPostcode) {
            updates['visitorAddressPostcode'] = this.billInfo.addressPostcode;
        }
        if (this.billInfo.addressCity) {
            updates['visitorAddressCity'] = this.billInfo.addressCity;
        }
        if (this.billInfo.addressCountry) {
            updates['visitorAddressCountry'] = this.billInfo.addressCountry;
        }
        if (this.billInfo.invoiceContactName) {
            updates['invoiceContactName'] = this.billInfo.invoiceContactName;
        }
        if (this.billInfo.invoiceAddress) {
            updates['invoiceAddress'] = this.billInfo.invoiceAddress;
        }
        if (this.billInfo.invoiceAddressHouseNumber) {
            updates['invoiceAddressHouseNumber'] = this.billInfo.invoiceAddressHouseNumber;
        }
        if (this.billInfo.invoiceAddressPostcode) {
            updates['invoiceAddressPostcode'] = this.billInfo.invoiceAddressPostcode;
        }
        if (this.billInfo.invoiceAddressCity) {
            updates['invoiceAddressCity'] = this.billInfo.invoiceAddressCity;
        }
        if (this.billInfo.invoiceAddressCountry) {
            updates['invoiceAddressCountry'] = this.billInfo.invoiceAddressCountry;
        }
        if (this.billInfo.deliveryContactName) {
            updates['deliveryContactName'] = this.billInfo.deliveryContactName;
        }
        if (this.billInfo.deliveryAddress) {
            updates['deliveryAddress'] = this.billInfo.deliveryAddress;
        }
        if (this.billInfo.deliveryAddressHouseNumber) {
            updates['deliveryAddressHouseNumber'] = this.billInfo.deliveryAddressHouseNumber;
        }
        if (this.billInfo.deliveryAddressPostcode) {
            updates['deliveryAddressPostcode'] = this.billInfo.deliveryAddressPostcode;
        }
        if (this.billInfo.deliveryAddressCity) {
            updates['deliveryAddressCity'] = this.billInfo.deliveryAddressCity;
        }
        if (this.billInfo.deliveryAddressCountry) {
            updates['deliveryAddressCountry'] = this.billInfo.deliveryAddressCountry;
        }

        this.billingInfoForm.patchValue(updates, { emitEvent: false });
    }

    private setupDeliveryValueChangeSubscriptions(): void {
        const fieldMappings: { [key: string]: string[] } = {
            deliveryContactName: ['contactName', 'visitorContactName', 'invoiceContactName'],
            deliveryAddress: ['visitorAddress', 'invoiceAddress'],
            deliveryAddressHouseNumber: ['visitorAddressHouseNumber', 'invoiceAddressHouseNumber'],
            deliveryAddressPostcode: ['visitorAddressPostcode', 'invoiceAddressPostcode'],
            deliveryAddressCity: ['visitorAddressCity', 'invoiceAddressCity'],
            deliveryAddressCountry: ['visitorAddressCountry', 'invoiceAddressCountry']
        };

        Object.keys(fieldMappings).forEach(sourceField => {
            const sub = this.billingInfoForm.get(sourceField)?.valueChanges
                .pipe(
                    distinctUntilChanged()) // Prevent duplicate triggers
                .subscribe(value => {
                    const updates: { [key: string]: any } = {};
                    const oldValue = value.slice(0, value.length - 1);

                    fieldMappings[sourceField].forEach(targetField => {
                        const targetValue = this.billingInfoForm.get(targetField)?.value;

                        if (value && !targetValue) {
                            // If source has a value but target is empty, copy value
                            updates[targetField] = value;
                        } else if (oldValue === targetValue) {
                            updates[targetField] = value;
                        } else {
                            return;
                        }
                    });

                if (Object.keys(updates).length > 0) {
                    this.billingInfoForm.patchValue(updates, { emitEvent: false });
                }
            });

            if (sub) {
                this.subscriptions.push(sub);
            }
        });
    }
    private setupInvoiceValueChangeSubscriptions(): void {
        const fieldMappings: { [key: string]: string[] } = {
            invoiceContactName: ['contactName', 'visitorContactName', 'deliveryContactName'],
            invoiceAddress: ['visitorAddress', 'deliveryAddress'],
            invoiceAddressHouseNumber: ['visitorAddressHouseNumber', 'deliveryAddressHouseNumber'],
            invoiceAddressPostcode: ['visitorAddressPostcode', 'deliveryAddressPostcode'],
            invoiceAddressCity: ['visitorAddressCity', 'deliveryAddressCity'],
            invoiceAddressCountry: ['visitorAddressCountry', 'deliveryAddressCountry']
        };

        Object.keys(fieldMappings).forEach(sourceField => {
            const sub = this.billingInfoForm.get(sourceField)?.valueChanges
                .pipe(distinctUntilChanged()) // Prevent duplicate triggers
                .subscribe(value => {
                    const updates: { [key: string]: any } = {};
                    const oldValue = value.slice(0, value.length - 1);

                    fieldMappings[sourceField].forEach(targetField => {
                        const targetValue = this.billingInfoForm.get(targetField)?.value;

                        if (value && !targetValue) {
                            // If source has a value but target is empty, copy value
                            updates[targetField] = value;
                        } else if (oldValue === targetValue) {
                            updates[targetField] = value;
                        } else {
                            return;
                        }
                    });

                if (Object.keys(updates).length > 0) {
                    this.billingInfoForm.patchValue(updates, { emitEvent: false });
                }
            });

            if (sub) {
                this.subscriptions.push(sub);
            }
        });
    }
    private setupVisitorValueChangeSubscriptions(): void {
        const fieldMappings: { [key: string]: string[] } = {
            visitorContactName: ['contactName', 'invoiceContactName', 'deliveryContactName'],
            visitorAddress: ['invoiceAddress', 'deliveryAddress'],
            visitorAddressHouseNumber: ['invoiceAddressHouseNumber', 'deliveryAddressHouseNumber'],
            visitorAddressPostcode: ['invoiceAddressPostcode', 'deliveryAddressPostcode'],
            visitorAddressCity: ['invoiceAddressCity', 'deliveryAddressCity'],
            visitorAddressCountry: ['invoiceAddressCountry', 'deliveryAddressCountry']
        };

        Object.keys(fieldMappings).forEach(sourceField => {
            const sub = this.billingInfoForm.get(sourceField)?.valueChanges
                .pipe(
                    startWith(this.billingInfoForm.get(sourceField)?.value)
                    , distinctUntilChanged()
                    , pairwise()) // Prevent duplicate triggers
                .subscribe(([oldValue, value]) => {
                    console.log(oldValue, value);
                    const direction = value > oldValue ? 1 : -1;

                    const updates: { [key: string]: any } = {};

                    fieldMappings[sourceField].forEach(targetField => {
                        const targetValue = this.billingInfoForm.get(targetField)?.value;

                        if (direction === -1 && oldValue !== targetValue) {
                            return;
                        }

                        if ((value && !targetValue) || direction === -1) {
                            // If source has a value but target is empty, copy value
                            updates[targetField] = value;
                        } else if (oldValue === targetValue) {
                            updates[targetField] = value;
                        } else {
                            return;
                        }
                    });

                if (Object.keys(updates).length > 0) {
                    this.billingInfoForm.patchValue(updates, { emitEvent: false });
                }
            });

            if (sub) {
                this.subscriptions.push(sub);
            }
        });
    }

    getSettings(): void {
        this.loading = true;
    }

    show(userId?: number, mymode?: boolean, email?: string): void {
        this._initBillingInfo();

        if (this.temporarySavedData) {
            this._userService.getUserForEdit(this.user.id).subscribe(userResult => {
                this.user = userResult.user as any;
                this.allOrganizationUnits = userResult.allOrganizationUnits;
                this.memberedOrganizationUnits = userResult.memberedOrganizationUnits;
                this.modal.show();
                this.userForm.form.markAllAsTouched();
            });
        } else {
            this.isValidateMode = mymode;
            if (!userId) {
                this.active = true;
                this.setRandomPassword = true;
                this.sendActivationEmail = true;
            }

            this._userService.getUserForEdit(userId).subscribe(userResult => {
                this.user = userResult.user as any;

                if (email) {
                    this.user.emailAddress = email;
                }

                this.roles = userResult.roles;
                this.canChangeUserName = this.user.userName !== AppConsts.userManagement.defaultAdminUserName;

                this.allOrganizationUnits = userResult.allOrganizationUnits;
                this.memberedOrganizationUnits = userResult.memberedOrganizationUnits;

                this.getProfilePicture(userResult.profilePictureId);

                if (userId) {
                    this.active = true;

                    setTimeout(() => {
                            this.setRandomPassword = false;
                        },
                        0);

                    this.sendActivationEmail = false;
                }

                this._profileService.getPasswordComplexitySetting().subscribe(passwordComplexityResult => {
                    this.passwordComplexitySetting = passwordComplexityResult.setting;
                    this.setPasswordComplexityInfo();
                });
                let listOfHostRoles = this.roles.filter(x => x.roleName === 'HostAdmin' || x.roleName === 'Support');
                if (listOfHostRoles.length === 0 && userId) {
                    this._userService.getVirtualBankAccount(userId).subscribe(result => {
                        this.billInfo = result;
                        this._setupBillingForm();
                        this.isSettingInvoiceEnabled = true;
                        this.modal.show();
                        this.userForm.form.markAllAsTouched();
                        },
                        error => {
                            this.isSettingInvoiceEnabled = false;
                            this.modal.show();
                            this.userForm.form.markAllAsTouched();
                        });
                } else {
                    if (listOfHostRoles.length !== 0) {
                        this.isSettingInvoiceEnabled = false;
                    }
                    this.modal.show();
                    this.userForm.form.markAllAsTouched();
                }
            });
        }
        this.temporarySavedData = false;

    }

    getBInf(): any {
        return this.billInfo ?? this.settings?.billing;
    }

    setPasswordComplexityInfo(): void {

        this.passwordComplexityInfo = '<ul>';

        if (this.passwordComplexitySetting.requireDigit) {
            this.passwordComplexityInfo += '<li>' + this.l('PasswordComplexity_RequireDigit_Hint') + '</li>';
        }

        if (this.passwordComplexitySetting.requireLowercase) {
            this.passwordComplexityInfo += '<li>' + this.l('PasswordComplexity_RequireLowercase_Hint') + '</li>';
        }

        if (this.passwordComplexitySetting.requireUppercase) {
            this.passwordComplexityInfo += '<li>' + this.l('PasswordComplexity_RequireUppercase_Hint') + '</li>';
        }

        if (this.passwordComplexitySetting.requireNonAlphanumeric) {
            this.passwordComplexityInfo += '<li>' + this.l('PasswordComplexity_RequireNonAlphanumeric_Hint') + '</li>';
        }

        if (this.passwordComplexitySetting.requiredLength) {
            this.passwordComplexityInfo += '<li>' + this.l('PasswordComplexity_RequiredLength_Hint', this.passwordComplexitySetting.requiredLength) + '</li>';
        }

        this.passwordComplexityInfo += '</ul>';
    }

    getProfilePicture(profilePictureId: string): void {
        if (!profilePictureId) {
            this.profilePicture = this.appRootUrl() + 'assets/common/images/default-profile-picture.png';
        } else {
            this._profileService.getProfilePictureById(profilePictureId).subscribe(result => {

                if (result && result.profilePicture) {
                    this.profilePicture = 'data:image/jpeg;base64,' + result.profilePicture;
                } else {
                    this.profilePicture = this.appRootUrl() + 'assets/common/images/default-profile-picture.png';
                }
            });
        }
    }

    onShown(): void {
        if (this.permissionChecker.isGranted(BasePermissionDefinition.OrganizationUnit.ManageOrganizationTree)) {
            if (!this.temporarySavedData) {
                this.organizationUnitTree.data = <IOrganizationUnitsTreeComponentData>{
                    allOrganizationUnits: this.allOrganizationUnits,
                    selectedOrganizationUnits: this.memberedOrganizationUnits
                };
            }
        }

        document.getElementById('Name').focus();
    }

    save(): void {
        if (!this.temporarySavedData) {
            let user = _.cloneDeep(this.user);
            user.virtualBankAccount = this._mapToVirtualBank();

            if (this.isCustomerMode) {
                user.userName = this._generateUserName(user);
                user.emailAddress = this._generateEmail(user);
            }

            let assignedRoleNames =
                _.map(
                    _.filter(this.roles, { isAssigned: true, inheritedFromOrganizationUnit: false }), role => role.roleName
                );
            const organizationUnits = this.organizationUnitTree?.getSelectedOrganizations() ?? [];

            //the invoices info also should be saved

            const input = {
                user: user,
                setRandomPassword: this.setRandomPassword,
                sendActivationEmail: this.sendActivationEmail,
                assignedRoleNames: assignedRoleNames,
                allowRemovingOrganizationUnits: this.allowRemovingOrganizationUnits,
                postProcessingTarget: this.permissionChecker.isGranted('Pages.App.Webshop.WMS') ? 'CreateCustomer' : undefined,
                organizationUnits: organizationUnits
            } as ICreateOrUpdateUserInputOfUserEditDto;

            this.saving = true;
            this._userService.createOrUpdateUser(new CreateOrUpdateUserInputOfUserEditDto(input))
                .pipe(finalize(() => { this.saving = false; }))
                .subscribe(() => {
                    this.notify.info(this.l('SavedSuccessfully'));
                    this.close();
                    this.modalSave.emit(null);
                });
            }
    }
    setValidAndSave() {
        let user = this.user;
        user.isValid = true;
        user.virtualBankAccount = this._mapToVirtualBank();
        let assignedRoleNames = _.map(
            _.filter(this.roles, { isAssigned: true, inheritedFromOrganizationUnit: false }), role => role.roleName
        );
        const organizationUnits = this.organizationUnitTree?.getSelectedOrganizations() ?? [];

        const input = {
            user: user,
            setRandomPassword: this.setRandomPassword,
            sendActivationEmail: this.sendActivationEmail,
            assignedRoleNames: assignedRoleNames,
            allowRemovingOrganizationUnits: this.allowRemovingOrganizationUnits,
            postProcessingTarget: this.permissionChecker.isGranted('Pages.App.Webshop.WMS') ? 'CreateCustomer' : undefined,
            organizationUnits: organizationUnits
        } as ICreateOrUpdateUserInputOfUserEditDto;

        this.saving = true;
        this._userService.createOrUpdateUser(new CreateOrUpdateUserInputOfUserEditDto(input))
            .pipe(finalize(() => { this.saving = false; }))
            .subscribe(() => {
                this.notify.info(this.l('SavedSuccessfully'));
                this.close();
                this.modalSave.emit(null);
            });
    }
    openPaymentTermsModal(isCustomer: boolean = false) {
        this.openPaymentTerms.emit(isCustomer);
    }

    close(): void {
        this.active = false;
        this.userPasswordRepeat = '';
        this.billInfo = new VirtualBankAccountDto();
        this.modal.hide();
    }

    getAssignedRoleCount(): number {
        return _.filter(this.roles, { isAssigned: true }).length;
    }

    openOUModal() {
        this.openSelectionOU.emit(this.user);
        this.temporarySavedData = true;
        this.modal.hide();
    }

    openAndAddSelectedOUs(selectedOUs: TreeNode[]) {
        this.show();
    }

    get isInvoiceBillingInformationFormValid(): boolean {
        if (this.permissionChecker.isGranted(BasePermissionDefinition.User.UserManagement._)) {
            return this.billingInfoForm?.invalid;
        }
        return false;
    }

    //#region Customer
    private _generateUserName(user: UserEditDto): string {
        if (!user) {
            throw new Error('Invalid input: Name, surname, and date are required.');
        }

        // Normalize and format name
        const normalizedName = user.name.trim().toLowerCase().replace(/\s+/g, '');
        const normalizedSurname = user.surname.trim().toLowerCase().replace(/\s+/g, '');

        // Get the current ISO timestamp
        const timestamp = new Date().toISOString().replace(/[-:.TZ]/g, ''); // Remove unwanted characters

        // Generate username
        return `${normalizedName}.${normalizedSurname}${timestamp}`;
    }

    private _generateEmail(user: UserEditDto): string {
        if (!user) {
            throw new Error('Invalid input: Name and surname are required.');
        }

        // Get the current ISO timestamp
        const timestamp = new Date().toISOString().replace(/[-:.TZ]/g, ''); // Remove unwanted characters

        // Extract first 3 letters (or use full name if shorter)
        const namePart = user.name.trim().toLowerCase().slice(0, 3).padEnd(3, 'x');
        const surnamePart = user.surname.trim().toLowerCase().slice(0, 3).padEnd(3, 'x');

        // Generate email
        return `${timestamp}${namePart}${surnamePart}@mcohealth.apprx.eu`;
    }

    //#endregion Customer


}
